// SettingsNamePane.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useWorkspace } from 'context/WorkspaceContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { updateWorkspace } from 'services/workspaces/WorkspaceWriteService';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';

/**
 * SettingsNamePane
 *
 * Renders an editable field for the workspace display name.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} workspace - Active workspace model.
 * @param {boolean} canEditWorkspace - Whether the current user can edit workspace settings.
 *
 * @returns {ReactNode} The rendered name settings pane.
 */
const SettingsNamePane = React.memo(({
    theme,
    workspace,
    canEditWorkspace,
}) => {

    // ----- CONTEXT ----- //

    // Workspace Context
    const { setActiveWorkspace } = useWorkspace();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    // Controlled input value.
    const [nameValue, setNameValue] = useState(workspace?.title || '');

    // Save in-flight state.
    const [isSaving, setIsSaving] = useState(false);

    // Error message state.
    const [errorMessage, setErrorMessage] = useState('');

    // Status message state.
    const [statusMessage, setStatusMessage] = useState('');

    // ----- EFFECTS ----- //

    // Keep the input in sync when the workspace prop changes.
    useEffect(() => {
        setNameValue(workspace?.title || '');
    }, [workspace?.title]);

    // ----- MEMOS ----- //

    // Whether the value differs from the persisted title.
    const hasChanges = nameValue.trim() !== (workspace?.title || '').trim();

    // ----- HANDLERS ----- //

    /**
     * Updates controlled input value and clears stale messages.
     *
     * @param {object} event - Native input change event.
     */
    const handleChange = (event) => {
        setNameValue(event.target.value);
        setErrorMessage('');
        setStatusMessage('');
    };

    /**
     * Persists the new workspace name via the write service.
     */
    const handleSave = async () => {
        if (isSaving || !workspace?.key) return;

        const trimmed = nameValue.trim();
        if (!trimmed) {
            setErrorMessage('Workspace name cannot be empty.');
            return;
        }

        setErrorMessage('');
        setStatusMessage('Saving...');
        setIsSaving(true);

        try {
            await updateWorkspace(workspace.key, { title: trimmed });
            setActiveWorkspace((previous) => {
                if (!previous || previous.key !== workspace.key) return previous;
                return { ...previous, title: trimmed };
            });
            setStatusMessage('Workspace name updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update workspace name.');
            setStatusMessage('');
        } finally {
            setIsSaving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                }}>
                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        Name
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        Workspace display name.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div className="settings-modal-name-card" style={{ borderColor: theme.backgroundHover }}>
                    <TextInput
                        value={nameValue}
                        onChange={handleChange}
                        onEnterPress={hasChanges ? handleSave : undefined}
                        disabled={!canEditWorkspace || isSaving}
                        maxLength={100}
                        width="100%"
                    />

                    <Button
                        iconKey="save"
                        label="Save"
                        onClick={handleSave}
                        enabled={canEditWorkspace && hasChanges && !isSaving}
                    />
                </div>

                {!canEditWorkspace && (
                    <div className="settings-modal-note" style={{ color: theme.foregroundMuted }}>
                        Only admins and owners can update the workspace name.
                    </div>
                )}

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}
            </div>
        </>
    );
});

// Dev Tools
SettingsNamePane.displayName = 'SettingsNamePane';

export default SettingsNamePane;
